import type { OcclusionStudyMode, ReviewCard, ReviewMask } from '../../../sdk/@types/Revisao';
import { layoutDaForma, type FormaAutoria } from './formaAutoria';
import { resolveStudyMode } from '../OcclusionEditor/occlusionStudy';

/**
 * Contrato da pré-visualização ao vivo do compositor (design, Decisão 10).
 * A tela monta estes campos; `CompositorPreview.tsx` desenha um `ReviewCard`.
 * Este arquivo não pode se chamar `compositorPreview.ts`: no macOS isso
 * colide com o componente e o TypeScript deixa de ver o default export.
 * O cartão de rascunho não é gravado — `id` é fixo para a face não remontar
 * a cada tecla.
 */

export type FacePrevia = 'frente' | 'verso';

export const PREVIEW_CARD_ID = 'compositor-preview';

export interface CamposDaPrevia {
  forma: FormaAutoria;
  front: string;
  back: string;
  frontImageKey?: string;
  masks: readonly ReviewMask[];
  /** Só entra no cartão quando a forma é oclusão. Ausente vira `ocultar-todas`. */
  studyMode?: OcclusionStudyMode;
  subjectId?: string;
  tags?: readonly string[];
}

export interface CompositorPreviewProps extends CamposDaPrevia {
  /** Nome da matéria, o mesmo que a sessão passa a `ReviewCard`. */
  subjectName?: string;
  face: FacePrevia;
  onFaceChange: (face: FacePrevia) => void;
  /**
   * Quando a imagem não pode ir pela leitura autenticada (história, URL já
   * pronta). Omitir deixa `ReviewCard` buscar a chave com `useAuthedImage`.
   */
  resolveImageUrl?: (fileKey: string) => string;
}

/**
 * O rascunho que o aluno veria. Texto, imagem e lista são `basic` e levam
 * `front`/`back` crus — `CardMarkdown`, dentro de `ReviewCard`, é quem
 * renderiza. Oclusão leva a imagem, as marcações e o modo, e não o `front`
 * de texto — mas leva `back`: é a explicação do verso (design, Decisão 8).
 */
export function cartaoDaPrevia(campos: CamposDaPrevia): ReviewCard {
  const layout = layoutDaForma(campos.forma);
  const oclusao = layout === 'occlusion';
  return {
    id: PREVIEW_CARD_ID,
    source: 'flashcard',
    layout,
    subjectId: campos.subjectId ?? '',
    tags: campos.tags ? [...campos.tags] : undefined,
    status: 'new',
    dueDate: '1970-01-01T00:00:00.000Z',
    intervalDays: 0,
    front: oclusao ? undefined : campos.front,
    back: campos.back,
    frontImageKey: oclusao ? campos.frontImageKey : undefined,
    masks: oclusao ? [...campos.masks] : undefined,
    studyMode: oclusao ? resolveStudyMode(campos.studyMode) : undefined,
  };
}
